import { type FC, useCallback, useState } from "react";
import { useBoolean } from "usehooks-ts";

import { useCanDeleteActions } from "../../../../../utils/delete";

import { DurationDisplayBox } from "./DurationDisplayBox";
import { PageListItem } from "./PageListItem";

import { HoverableEditField } from "components/hover-widgets/EditableField";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import {
  EstimatedTimeMenu,
  TimesType,
} from "components/materials/topic/activity/detail/EstimatedTimeMenu";
import { ActivitySidebarOptionsMenu } from "components/menus/ActivitySidebarOptionsMenu";
import { ActivityPageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { DragAndDrop, DraggableItemType } from "mds/components/DragAndDrop";
import { TextAddButton } from "mds/components/TextAddButton";
import { useOrdered } from "mds/hooks/use-ordered";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  ActivityWithPagesType,
  selectCurrentCourseId,
  selectIsEditModeEnabled,
  selectPageById,
} from "store/selectors";
import {
  copyActivity,
  isArchived,
  moveActivityWithinCourse,
  useActivityActions,
} from "utils/activity";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { useDnDItemOrdering, useLocalCopy } from "utils/collections";
import { changeReleaseStatusForPages, getReleaseStatus, usePageActions } from "utils/page";
import { useParams } from "utils/routing";
import { pageUrl } from "utils/urls";

const DEFAULT_TIME_ESTIMATE = 0;

type ActivityCardProps = {
  activity: ActivityWithPagesType;
  activityIndex: number;
  activities: ActivityWithPagesType[];
  moveActivityLocally: (item: DraggableItemType, toIndex: number, dropContainerId: string) => void;
  shouldDisplayActivityNumber?: boolean;
};

/* This is the card used to display Lesson and Archive activities. Additional Resources
   and Prep Work activities are displayed via AdditionalResourceList and PrepWorkActivityCard
   respectively. If you are doing an update here, you likely want to check and update the
   other components.
   TODO: We should probably integrate these components into a single ActivityCard component,
   or rename this one as we've introduced a number of bugs due to inconsistently updating
   the different components as the design evolves.
 */
export const ActivityCard: FC<ActivityCardProps> = ({
  activityIndex,
  activity,
  activities,
  moveActivityLocally,
  shouldDisplayActivityNumber = true,
}) => {
  const pathParams = useParams();
  const pageId = pathParams.pageId;
  const { canAuthorCourse } = useCapabilities();
  const courseId = useAppSelector(selectCurrentCourseId);
  const [_pages, setPages] = useLocalCopy(activity.pages);
  const selectedPage = useAppSelector((s) => selectPageById(s, pageId));
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const pages = useOrdered(_pages);
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const activityHasPages = activity.pages.length > 0;

  const { canDeleteActivity } = useCanDeleteActions(activity.topic_id, pages);

  const movePageLocally = useDnDItemOrdering(pages, setPages, "activity_id");
  const isActive = selectedPage?.activity_id === activity.id;
  const { saveActivity, deleteActivity, dropActivity, unarchiveActivity, archiveActivity } =
    useActivityActions();
  const { createPage, dropPage, selectPage, navigateToPage } = usePageActions();

  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);

  const activityIsArchived = isArchived(activity.category);
  const activityReleaseStatus = getReleaseStatus(pages);

  const createNewPage = () => {
    createPage("activity_page", activity.id);
    trackEvent("Activity - Add page", {
      ...BUTTON_EVENT_CATEGORY,
    });
  };

  const onDropPage = (droppedPage: ActivityPageType) => dropPage(droppedPage, activities);

  const onActivityTitleChanged = (newTitle: string) => {
    saveActivity({ ...activity, title: newTitle });
  };

  const onActivityEstimatedTimeChanged = (newTime: number) => {
    saveActivity({ ...activity, estimated_time: newTime });
  };

  const onDelete = useCallback(() => {
    trackEvent("Activity - Delete activity", {
      ...BUTTON_EVENT_CATEGORY,
    });
    if (activityHasPages) {
      openConfirmationDialog();
    } else {
      deleteActivity(activity);
    }
  }, [activity, deleteActivity, openConfirmationDialog, activityHasPages]);

  return (
    <Card className="mb-3 mt-2" isActive={isActive} role="list" small>
      <DragAndDrop
        draggable={canAuthorCourse && isEditModeEnabled && !isEditingTitle}
        dragItemKind="activity"
        dropContainerId={activity.category}
        dropIndex={activityIndex}
        droppable={canAuthorCourse && isEditModeEnabled}
        item={activity}
        onDraggedItemHover={moveActivityLocally}
        onDropItem={dropActivity}
      >
        <DragAndDrop
          dragItemKind="page"
          dropContainerId={activity.id}
          dropIndex={0}
          droppable={canAuthorCourse && isEditModeEnabled}
          onDraggedItemHover={movePageLocally}
          onDropItem={onDropPage}
        >
          <div className="mb-1 flex w-full items-start justify-between">
            {/* TODO: Fix: students and instructors might see different numbers because unpublished activities are skipped for students */}
            <HoverableToolbar
              className="ml-1 mr-2 flex w-full items-center justify-between"
              contentClassName="mt-0.5 bg-white"
              readOnly={!canAuthorCourse}
              reference="activity"
              disableUnlessEditMode
              showEditButton
              uncentered
              onEditModeChange={setIsEditingTitle}
            >
              <h3
                className="my-0 flex w-full cursor-pointer items-center font-semibold"
                onClick={() => pages.length > 0 && selectPage(pages[0])}
              >
                {shouldDisplayActivityNumber && (
                  <div className="h3 mt-1 self-start pr-1">{activityIndex + 1}.</div>
                )}

                <HoverableEditField
                  className="pl-[3px]"
                  label={t("glossary.activity_title")}
                  textSize="h3"
                  value={activity.title}
                  disableUnlessEditMode
                  shouldWrap
                  onValueChanged={onActivityTitleChanged}
                />
              </h3>

              <ActivitySidebarOptionsMenu
                currentTopicId={activity.topic_id}
                optionsMenuActions={{
                  onAddPage: createNewPage,
                  onArchive: activityIsArchived ? null : () => archiveActivity(activity),
                  onUnarchive: activityIsArchived ? () => unarchiveActivity(activity) : null,
                  onDelete,
                  isDeleteDisabled: !canDeleteActivity(activity),
                  deleteTooltipText: canDeleteActivity(activity)
                    ? undefined
                    : t("tooltip.delete_activity_has_assessments"),
                  onDuplicate: () => copyActivity(activity.id),
                  onMoveTo: (destinationTopicId) =>
                    moveActivityWithinCourse(activity.id, destinationTopicId),
                  ...(!activityIsArchived
                    ? { onChangeStatus: () => changeReleaseStatusForPages(activity.pages) }
                    : {}),
                }}
                releaseStatus={activityReleaseStatus}
              />
            </HoverableToolbar>

            {canAuthorCourse && isEditModeEnabled && (
              <EstimatedTimeMenu
                selectedTime={(activity.estimated_time || DEFAULT_TIME_ESTIMATE) as TimesType}
                onClick={onActivityEstimatedTimeChanged}
              />
            )}

            {canAuthorCourse && !isEditModeEnabled && !!activity.estimated_time && (
              <div className="mt-2">
                <DurationDisplayBox duration={activity.estimated_time} />
              </div>
            )}
          </div>
        </DragAndDrop>

        {pages.map((page: ActivityPageType, index) => (
          <PageListItem
            containerId={activity.id}
            key={page.id}
            page={page}
            pageIndex={index}
            topicId={activity.topic_id}
            onDropPage={onDropPage}
            onEditingTitle={setIsEditingTitle}
            onMovePageLocally={movePageLocally}
            onPageClick={() =>
              navigateToPage(page, pageUrl(courseId, page.id, { topicId: activity.topic_id }))
            }
          />
        ))}
      </DragAndDrop>

      {canAuthorCourse && (
        <div className="h4 -ml-1 flex items-center justify-start">
          <TextAddButton reference="page" hideUnlessEditMode underlined onClick={createNewPage} />
        </div>
      )}

      <ConfirmDeletionDialog
        glossaryKey="activity"
        instanceName={activity.title}
        open={confirmationDialogIsOpen}
        onClose={closeConfirmationDialog}
        onDelete={() => deleteActivity(activity)}
      />
    </Card>
  );
};
